import { createFileRoute } from '@tanstack/react-router';
import { LoadFailed } from '../components/QueryState';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { InviteDialog } from '../features/users/InviteDialog';
import { MemberRow } from '../features/users/MemberRow';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/settings/users')({
  component: UsersPage,
});

/**
 * Everyone in the organisation you are currently acting in.
 *
 * There is no organisation picker on this page and there is no `orgId` in any request it
 * makes. Scope comes from the session — switch organisation in the account menu and this
 * page is about a different agency, with a different list, because the server says so.
 */
function UsersPage() {
  const queryClient = useQueryClient();
  const members = useQuery(
    trpc.users.list.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load the users in this organisation' },
      staleTime: 15_000,
    }),
  );

  // One instant for the whole render, so two rows cannot disagree about whether the same
  // lockout has expired.
  const now = new Date();

  // The server already drops lapsed support access, so this is only about the copy sitting in
  // the query cache: a grant is at most eight hours long and this page is one an administrator
  // leaves open, so the row can lapse between refetches. Applying the same rule on render means
  // the list stops showing them at the next render rather than the next fetch. The server stays
  // the authority — nothing here is a permission check.
  const visible = members.data
    ? members.data.members.filter(
        (member) => member.supportExpiresAt === null || new Date(member.supportExpiresAt) > now,
      )
    : [];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.users.list.queryKey() });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Users"
        subtitle={members.data ? `${visible.length} in this organisation` : 'Users and their roles'}
        actions={
          members.data ? (
            <InviteDialog assignableRoles={members.data.assignableRoles} onInvited={refresh} />
          ) : null
        }
      />

      <SettingsNav />

      {members.isError ? (
        <Panel className="p-6">
          <LoadFailed inline retry={() => void members.refetch()} retrying={members.isFetching} />
        </Panel>
      ) : null}

      {members.isPending ? (
        <Panel className="p-6">
          <p className="text-[0.875rem] text-ink-muted">Loading…</p>
        </Panel>
      ) : null}

      {members.data ? (
        <Panel>
          <PanelHeader
            title="Members"
            subtitle={
              members.data.orgKind === 'platform'
                ? 'ProManage staff. These roles can see every agency and issue support access; none of them can post a journal.'
                : 'Roles decide what each person can reach. Only Accounts and Trust administrator can move money.'
            }
          />

          <ul className="border-t border-rule">
            {visible.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                assignableRoles={members.data.assignableRoles}
                now={now}
                onChanged={refresh}
              />
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}
